/* Sticky add-to-cart bar. Mirrors the main product form's price and button
   state, shows itself once the main buy button scrolls out of view, and
   delegates the actual add to the real form button (so the theme's AJAX add +
   cart drawer keep working). */

if (!customElements.get('sticky-atc')) {
  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.form = document.getElementById(this.dataset.formId);
      // Watch ONLY the product-info column, never the whole section and never
      // the sticky bar itself — otherwise our own writes (and the per-second
      // delivery timer) would retrigger the observer and freeze the page.
      this.watchTarget =
        document.getElementById(`product-info-${this.dataset.sectionId}`) ||
        this.form?.closest('.product-info') ||
        this.form;
      this.stickyBtn = this.querySelector('[data-sticky-atc-add]');
      this.stickyBtnText = this.querySelector('.sticky-atc__btn-text');
      this.stickyPrice = this.querySelector('[data-sticky-atc-price]');
      this.topBtn = this.querySelector('[data-sticky-atc-top]');
      this.syncScheduled = false;
    }

    connectedCallback() {
      if (!this.form || !this.watchTarget) return;

      this.hidden = false; // visibility is driven by the .is-visible class

      this.stickyBtn.addEventListener('click', this.handleClick.bind(this));

      this.topBtn?.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      // Reveal the bar when the main form has scrolled above the viewport.
      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
        threshold: 0
      });
      this.observer.observe(this.form);

      // Keep price + button state in sync when the variant changes. The theme
      // swaps out the price / add-to-cart nodes, so we re-read them on change
      // rather than holding references. Coalesced via rAF to avoid churn.
      this.mutation = new MutationObserver(this.scheduleSync.bind(this));
      this.mutation.observe(this.watchTarget, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled']
      });

      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.mutation?.disconnect();
    }

    getMainBtn() {
      return this.form.querySelector('[name="add"]');
    }

    handleIntersect(entries) {
      const entry = entries[0];
      // Only reveal after the form has scrolled UP past the top of the screen,
      // not while it's still below the fold on initial load.
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', scrolledPast);
    }

    handleClick() {
      const mainBtn = this.getMainBtn();
      if (!mainBtn) return;
      const disabled =
        mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true';
      if (disabled) return;
      mainBtn.click();
    }

    scheduleSync() {
      if (this.syncScheduled) return;
      this.syncScheduled = true;
      requestAnimationFrame(() => {
        this.syncScheduled = false;
        this.sync();
      });
    }

    sync() {
      const mainBtn = this.getMainBtn();
      if (mainBtn) {
        const disabled =
          mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true';
        if (this.stickyBtn.disabled !== disabled) this.stickyBtn.disabled = disabled;
        const label = mainBtn.textContent.trim();
        if (label && this.stickyBtnText && this.stickyBtnText.textContent !== label) {
          this.stickyBtnText.textContent = label;
        }
      }

      const mainPrice = this.watchTarget.querySelector('.product-info__price');
      if (mainPrice && this.stickyPrice && this.stickyPrice.innerHTML !== mainPrice.innerHTML) {
        this.stickyPrice.innerHTML = mainPrice.innerHTML;
      }
    }
  }

  customElements.define('sticky-atc', StickyAtc);
}
